import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldOff, Trash2, Loader2, ShieldCheck, UserPlus, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useAuth } from '../context/AuthContext';
import { listAllUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } from '../lib/api';
import { toast } from '../hooks/use-toast';

const AdminUsersView = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAllUsers();
      setUsers(data);
    } catch (err) {
      toast({ title: 'Failed to load users', description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [adding, setAdding] = useState(false);

  const addUser = async () => {
    try {
      setAdding(true);
      await adminCreateUser(addForm);
      toast({ title: 'User created' });
      setAddForm({ email: '', name: '', password: '', role: 'user' });
      setAddOpen(false);
      await load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Could not create user', variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    try {
      setResetting(true);
      await adminUpdateUser(resetTarget.user_id, { password: resetPassword });
      toast({ title: `Password reset for ${resetTarget.email}` });
      setResetTarget(null);
      setResetPassword('');
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Could not reset password', variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  const promoteToAdmin = async (u) => {
    if (!confirm(`Promote ${u.email} to admin?`)) return;
    try {
      const updated = await adminUpdateUser(u.user_id, { role: 'admin' });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? updated : x)));
      toast({ title: 'Promoted to admin' });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message });
    }
  };

  const demoteToUser = async (u) => {
    if (!confirm(`Demote ${u.email} to user?`)) return;
    try {
      const updated = await adminUpdateUser(u.user_id, { role: 'user' });
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? updated : x)));
      toast({ title: 'Demoted to user' });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message });
    }
  };

  const removeUser = async (u) => {
    if (!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await adminDeleteUser(u.user_id);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast({ title: 'User deleted' });
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message });
    }
  };

  if (me?.role !== 'admin') {
    return (
      <div className="p-12 text-center text-slate-500 max-w-lg mx-auto">
        <Shield className="h-10 w-10 mx-auto mb-4 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Admin only</h2>
        <p className="text-sm">Only admins can view and manage system users.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Admins have full access to every board. Users are assigned per-board with category
            restrictions.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-[#0090D8] hover:bg-[#0079B5] text-white shrink-0"
        >
          <UserPlus className="h-4 w-4 mr-1.5" />
          Add User
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 justify-center py-8 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading users...
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  User
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Role
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Joined
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isMe = u.user_id === me.user_id;
                const isAdmin = u.role === 'admin';
                return (
                  <tr key={u.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white text-xs font-semibold">
                            {(u.name || u.email)[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-800">
                            {u.display_name || u.name || '—'}
                            {isMe && <span className="ml-2 text-[10px] text-slate-500">(you)</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      {isAdmin ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300">
                          <ShieldCheck className="h-3 w-3" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-300">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      {isAdmin ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => demoteToUser(u)}
                          disabled={isMe}
                        >
                          <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
                          Demote
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => promoteToAdmin(u)}
                        >
                          <Shield className="h-3.5 w-3.5 mr-1.5" />
                          Make admin
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setResetTarget(u);
                          setResetPassword('');
                        }}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                        Reset password
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeUser(u)}
                        disabled={isMe}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new account. The user can sign in immediately with the password below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                className="mt-1.5"
                placeholder="name@example.com"
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                className="mt-1.5"
                placeholder="Full name"
              />
            </div>
            <div>
              <Label>Initial password</Label>
              <Input
                type="password"
                value={addForm.password}
                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                className="mt-1.5"
                placeholder="Temporary password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={addForm.role}
                onValueChange={(v) => setAddForm({ ...addForm, role: v })}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              onClick={addUser}
              disabled={adding || !addForm.email || !addForm.password}
              className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
            >
              {adding ? 'Creating...' : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              {resetTarget ? `Set a new password for ${resetTarget.email}.` : ''}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>New password</Label>
            <Input
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className="mt-1.5"
              placeholder="New password"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button
              onClick={submitResetPassword}
              disabled={resetting || !resetPassword}
              className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
            >
              {resetting ? 'Resetting...' : 'Reset password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersView;
